import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  Stack 
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { landingThemeConfig } from '../styles/landingThemeConfig';

const faqData = [
  {
    question: "Are these real HSC questions? How do I know they're relevant?",
    answer: "Absolutely. Every question in our database is from a past HSC or official trial paper. They are then vetted and categorized by expert tutors with years of HSC marking experience to ensure they are 100% syllabus-aligned. You're practicing with the real deal."
  },
  {
    question: "Who is behind this?",
    answer: "We're a team of experienced HSC tutors and AI developers who were frustrated with how hard it was to find targeted practice materials. We built this tool for our own students first. Our goal is to combine expert teaching knowledge with smart technology to give you the most efficient study tool possible."
  },
  {
    question: "How is this better than just using my textbook or practice papers?",
    answer: "Textbooks give you questions. We give you precision. Instead of doing a whole random paper, you can instantly create a targeted exam on the exact topics you're struggling with, at the difficulty you need. It's the difference between a shotgun and a laser."
  },
  {
    question: "Do you provide worked solutions, or just the final answers?",
    answer: "Currently, our system provides final answers to check your work instantly. However, full step-by-step worked solutions and AI-powered hints are the #1 feature on our development roadmap. By joining the waitlist, you'll be the first to get access and help us shape this feature."
  },
  {
    question: "What subjects do you currently cover?",
    answer: "For our early access phase, we are laser-focused on HSC Mathematics Advanced, Extension 1, and Extension 2. We plan to expand to more subjects based on user feedback. Let us know what you'd like to see next!"
  },
  {
    question: "What does \"Early Access\" mean? What happens after I join the waitlist?",
    answer: "It means you're first in line. After joining the waitlist, you'll receive a confirmation email. We are letting in small groups of students to test the platform and gather feedback. As a thank you for being an early supporter, you'll also get access to an exclusive 'Founder's Deal' when we open up spots."
  }
];

const StudentFAQ = ({ colorTheme = 'teal' }) => {
  const studentTheme = landingThemeConfig.student(colorTheme);
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        position: 'relative',
        backgroundColor: 'transparent',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="h2"
            sx={{
              mb: 2,
              fontWeight: 700,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: '1.125rem' }}
          >
            Got questions? We've got answers.
          </Typography>
        </Box>

        <Stack spacing={1} sx={{ maxWidth: '900px', mx: 'auto' }}>
          {faqData.map((item, index) => {
            const panelId = `panel-${index}`;
            return (
              <Accordion
                key={index}
                expanded={expanded === panelId}
                onChange={handleChange(panelId)}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  border: `1px solid ${studentTheme.components.demo.border}`,
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 8px 0',
                    borderColor: 'primary.main',
                    boxShadow: `0 0 20px ${studentTheme.components.button.primaryShadow}`,
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 3,
                    py: 2,
                    '& .MuiAccordionSummary-content': {
                      my: 0,
                    },
                    '&.Mui-expanded': {
                      minHeight: 'auto',
                    },
                  }}
                >
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 600,
                      color: expanded === panelId ? 'primary.main' : 'text.primary',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.7,
                      fontSize: '0.95rem',
                    }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
};

export default StudentFAQ;